import { useEffect, useRef, useState } from 'react';
import { Copy, MoreHorizontal, Play, Trash2, Undo2 } from 'lucide-react';
import type { FidsTrackingRouteStatus } from 'contracts/FidsTrackingRoutes';
import * as S from './styles';

interface Props {
  status: FidsTrackingRouteStatus;
  // Motivo para não ativar (sem centro, sem armazém), ou null.
  activationIssue: string | null;
  hasChanges: boolean;
  isBusy: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const SAVE_FIRST = 'Salve ou descarte as alterações antes.';

// Ações do rastreio: ativar ou voltar para rascunho, e o menu com Duplicar e Excluir.
// Ativar, voltar e duplicar usam o rastreio salvo, por isso pedem as alterações salvas.
export const TrackingRouteActions: React.FC<Props> = ({
  status,
  activationIssue,
  hasChanges,
  isBusy,
  onActivate,
  onDeactivate,
  onDuplicate,
  onDelete,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node))
        setIsMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMenuOpen]);

  const choose = (action: () => void) => {
    setIsMenuOpen(false);
    action();
  };

  return (
    <>
      {status === 'active' ? (
        <S.SecondaryButton
          type="button"
          disabled={isBusy || hasChanges}
          title={hasChanges ? SAVE_FIRST : undefined}
          onClick={onDeactivate}
        >
          <Undo2 size={16} aria-hidden="true" />
          Voltar para rascunho
        </S.SecondaryButton>
      ) : (
        <S.OutlineButton
          type="button"
          disabled={isBusy || hasChanges || Boolean(activationIssue)}
          title={hasChanges ? SAVE_FIRST : (activationIssue ?? undefined)}
          onClick={onActivate}
        >
          <Play size={16} aria-hidden="true" />
          Ativar rastreio
        </S.OutlineButton>
      )}
      <div ref={menuRef} className="relative">
        <S.SecondaryButton
          type="button"
          aria-label="Mais ações do rastreio"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          disabled={isBusy}
          className="px-3"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <MoreHorizontal size={18} aria-hidden="true" />
        </S.SecondaryButton>
        {isMenuOpen ? (
          <div
            role="menu"
            aria-label="Ações do rastreio"
            className="absolute right-0 z-20 mt-1 flex w-48 flex-col rounded border border-slate-200 bg-white py-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              disabled={hasChanges}
              title={hasChanges ? SAVE_FIRST : undefined}
              onClick={() => choose(onDuplicate)}
              className="flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Copy size={15} aria-hidden="true" />
              Duplicar
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(onDelete)}
              className="flex items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
            >
              <Trash2 size={15} aria-hidden="true" />
              Excluir
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
};
